import * as React from "react";
import { createPortal } from "react-dom";
import { API_Search } from "../../../src/apidefs";
import { AutocompletePane } from "../DesignSystem/AutocompletePane";
import { api } from "../api";
import { TestIds } from "../testIds";

interface SearchProps {}

interface SearchState {
  results: API_Search | undefined;
  autocompleteVisible: boolean;
  mobileExpanded: boolean;
}

export class Search extends React.Component<SearchProps, SearchState> {
  input: HTMLInputElement | undefined;

  state: SearchState = {
    results: undefined,
    autocompleteVisible: false,
    mobileExpanded: false,
  };

  async updateSearch(): Promise<void> {
    if (!this.input) {
      return;
    }

    const search = this.input.value.toLowerCase();

    if (search.trim() === "") {
      this.setState({ results: undefined });

      return;
    }

    this.setState(
      {
        results: await api.get<"/api/search/:search">(`/api/search/${search}`),
      },
      () => {
        if (this.state.results) {
          this.setState({ autocompleteVisible: true });
        }
      }
    );
  }

  clear(): void {
    this.setState({
      results: undefined,
      autocompleteVisible: false,
      mobileExpanded: false,
    });
    if (this.input) this.input.value = "";
  }

  hideAutocomplete() {
    this.setState({ autocompleteVisible: false });
  }

  toggleMobileSearch = () => {
    this.setState({ mobileExpanded: !this.state.mobileExpanded }, () => {
      if (this.state.mobileExpanded && this.input) {
        this.input.focus();
      }
    });
  };

  render(): JSX.Element {
    const currentQuery = this.input ? this.input.value : "";
    return (
      <div className="relative flex w-full items-center">
        {/* Mobile: Show icon when collapsed */}
        <button
          onClick={this.toggleMobileSearch}
          className={`rounded-full border border-slate-200 p-2 text-slate-700 transition hover:bg-slate-100 dark:border-white/15 dark:text-white dark:hover:bg-white/10 md:hidden ${
            this.state.mobileExpanded ? "hidden" : ""
          }`}
          aria-label="Search"
        >
          <i className="fa fa-search" aria-hidden="true"></i>
        </button>

        {/* Mobile: Full-screen search overlay (rendered via portal to escape navbar) */}
        {this.state.mobileExpanded &&
          createPortal(
            <div
              className="flex flex-col bg-white dark:bg-slate-900 md:hidden"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                zIndex: 100,
              }}
            >
              {/* Header with input and close button */}
              <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-white/10">
                <i className="fa fa-search text-slate-400 dark:text-white/50" aria-hidden="true"></i>
                <input
                  ref={(input) => {
                    if (input) this.input = input;
                  }}
                  type="text"
                  className="h-10 flex-1 appearance-none bg-transparent text-base font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white dark:placeholder:text-white/50"
                  placeholder="Search..."
                  data-testid={TestIds.SearchInput}
                  onInput={() => this.updateSearch()}
                  autoFocus
                />
                <button
                  onClick={() => this.clear()}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 dark:text-white/70 dark:hover:bg-white/10"
                  aria-label="Close search"
                >
                  <i className="fa fa-times text-lg" aria-hidden="true"></i>
                </button>
              </div>

              {/* Results area */}
              <div className="flex-1 overflow-y-auto p-4" onClick={() => this.clear()}>
                {this.state.results ? (
                  <AutocompletePane
                    results={this.state.results}
                    entries
                    users
                    paneHeight="tall"
                    query={currentQuery}
                  />
                ) : (
                  <div className="text-center text-sm text-slate-500 dark:text-white/50">
                    Start typing to search...
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}

        {/* Desktop: Always show search input */}
        <div className="relative hidden w-full items-center md:flex">
          <input
            ref={(input) => {
              if (input && !this.state.mobileExpanded) this.input = input;
            }}
            type="text"
            className="h-9 w-full appearance-none rounded-full border border-slate-300 bg-white pl-5 pr-10 text-sm font-medium leading-9 text-slate-900 placeholder:font-light placeholder:text-slate-500 shadow-sm shadow-black/5 transition focus:border-chips-gold/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-900/80 dark:text-white dark:placeholder:text-white/60 dark:shadow-black/20 dark:focus:bg-slate-900/95 dark:focus:ring-chips-gold/30"
            placeholder="search"
            data-testid={TestIds.SearchInput}
            onInput={() => this.updateSearch()}
            onBlur={() => {
              setTimeout(() => {
                this.hideAutocomplete();
                this.clear();
              }, 300);
            }}
          />

          <div className="absolute left-0 top-12 w-full max-w-lg">
            {this.state.autocompleteVisible && this.state.results && (
              <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl backdrop-blur dark:border-white/10 dark:bg-slate-900/95">
                <AutocompletePane
                  results={this.state.results}
                  entries
                  users
                  paneHeight="tall"
                  query={currentQuery}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
